import React, { useEffect, useRef, useState } from "react";
import { Box, Chip, Grid, styled, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import IotVisionTable from "components/common/IotVisionTable";
import PaperWrapper from "components/wrappers/PaperWrapper";
import { SortOrder } from "enums/sorting.enum";
import { useNavigate, useSearchParams } from "react-router-dom";
import { humanize } from "utils/common";
import SectionHeading from "components/headings/SectionHeading";
import { useSelector } from "react-redux";
import StatusCodes from "enums/status-code.enums";
import { IDeviceHeartbeat, IGetDeviceHeartbeatQuery } from "types/device";
import { format, formatDistance } from "date-fns";
import { executeGetDeviceHeartbeatRequest } from "api/app-taxi-ranking/taxi-location-layout.api";
import { DeviceType } from "enums/device.enums";
import IotVisionToolTips from "components/common/IotVisionToolTips";
import { DeviceHeartbeatStatus } from "enums/heartbeat.enum";
import { LoadingWrapper } from "components/wrappers/LoadingWrapper";
import DeviceMonitorFilterPanel from "./device-monitor-filter-panel";

interface StatsCardProps {
    title: string;
    content: number | string | null;
    isLoading?: boolean;
    contentColor?: string;
}

interface StyledProps {
    isLoading?: boolean;
}

interface DeviceHeartbeatWithLocation extends IDeviceHeartbeat {
    location: string;
}

const PAGE_SIZE = 10;
const DATE_FORMAT = "MMM d, yyyy, hh:mm aaa";
const UNKNOWN_HEARTBEAT_STATUS_TOOLTIP = "No heartbeats received for this device";

const CustomizedSectionHeading = styled(SectionHeading)(() => ({
    background: "transparent !important",
}));

const HeartbeatStatusChip = styled(Chip, {
    shouldForwardProp: (prop) => prop !== "status",
})<{ status: DeviceHeartbeatStatus }>(({ status }) => ({
    height: "24px",
    ...(status === DeviceHeartbeatStatus.CONNECTED
        ? {
              backgroundColor: "#DBFCE7",
              color: "#4B6B3B",
          }
        : {}),
    ...(status === DeviceHeartbeatStatus.DISCONNECTED
        ? {
              backgroundColor: "#FFE2E2",
              color: "#BE4E55",
          }
        : {}),
    ...(status === DeviceHeartbeatStatus.UNKNOWN
        ? {
              backgroundColor: "#FFF9C4",
              color: "#B76E00",
          }
        : {}),
    letterSpacing: "1px",
    fontWeight: 600,
    textAlign: "center",
    display: "inline-flex",
    alignItems: "center",
    "& .MuiChip-label": {
        lineHeight: "24px",
        padding: "0 12px",
    },
}));

const StatCardContainer = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.common.white,
    padding: "15px 23px",
    borderRadius: "10px",
    textAlign: "left",
    color: theme.palette.primary.main,
}));

const FigureWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isLoading",
})<StyledProps>(({ isLoading }) => ({
    display: "flex",
    alignItems: "center",
    marginTop: "15px",
    position: "relative",
    padding: isLoading ? "0" : "0",
    margin: isLoading ? undefined : undefined,
}));

const StatCard: React.FC<StatsCardProps> = ({ title, content, isLoading = false, contentColor = "#21315B" }) => (
    <StatCardContainer>
        <Typography variant="body2" fontWeight={600} sx={{ color: "#21315B" }}>
            {title}
        </Typography>
        <FigureWrapper isLoading={isLoading}>
            <LoadingWrapper isLoading={isLoading} className="loading-wrapper">
                <Typography variant="h4" position="relative" fontWeight={600} sx={{ color: contentColor }}>
                    {content}
                </Typography>
            </LoadingWrapper>
        </FigureWrapper>
    </StatCardContainer>
);

const tableColumns: GridColDef<DeviceHeartbeatWithLocation>[] = [
    {
        field: "type",
        headerName: "Device",
        flex: 1,
        minWidth: 100,
        maxWidth: 150,
        sortable: true,
        disableColumnMenu: true,
        renderCell: ({ row: { type } }) => (
            <Typography variant="body">
                {type.toUpperCase() === DeviceType.GPU ? type.toUpperCase() : humanize(type)}
            </Typography>
        ),
    },
    {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 300,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: ({ row: { name } }) => name,
        renderCell: ({ row: { name } }) => (
            <IotVisionToolTips title={name} arrow>
                <span>{name}</span>
            </IotVisionToolTips>
        ),
    },
    {
        field: "location",
        headerName: "Location",
        flex: 1,
        minWidth: 300,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: ({ row: { location } }) => location,
        renderCell: ({ row: { location } }) => (
            <IotVisionToolTips title={location} arrow>
                <span>{location}</span>
            </IotVisionToolTips>
        ),
    },
    {
        field: "heartbeat.status",
        headerName: "Status",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: ({ row: { heartbeat } }) => heartbeat?.status,
        sortComparator: (v1: DeviceHeartbeatStatus, v2: DeviceHeartbeatStatus) => v1?.localeCompare(v2 ?? ""),
        renderCell: ({ row: { heartbeat } }) => {
            const status = heartbeat?.status;
            const chip = status ? (
                <HeartbeatStatusChip status={status} label={status} />
            ) : (
                <HeartbeatStatusChip status={DeviceHeartbeatStatus.UNKNOWN} label={DeviceHeartbeatStatus.UNKNOWN} />
            );
            if (!status || status === DeviceHeartbeatStatus.UNKNOWN) {
                return (
                    <IotVisionToolTips title={UNKNOWN_HEARTBEAT_STATUS_TOOLTIP} arrow>
                        <span style={{ display: "inline-flex" }}>{chip}</span>
                    </IotVisionToolTips>
                );
            }
            return chip;
        },
    },
    {
        field: "heartbeat.lastUpdatedTime",
        headerName: "Last Seen",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: ({ row: { heartbeat } }) => heartbeat?.lastUpdatedTime || "",
        sortComparator: (v1: string, v2: string) => v1?.localeCompare(v2 ?? ""),
        renderCell: ({ row: { heartbeat } }) =>
            heartbeat?.lastUpdatedTime ? (
                <span>
                    {format(new Date(heartbeat?.lastUpdatedTime), DATE_FORMAT).replace("am", "AM").replace("pm", "PM")}
                </span>
            ) : null,
    },
    {
        field: "heartbeat.lastUpdatedTime.relative",
        headerName: "Last Seen Ago",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: ({ row: { heartbeat } }) => heartbeat?.lastUpdatedTime || "",
        sortComparator: (v1: string, v2: string) => v1?.localeCompare(v2 ?? ""),
        renderCell: ({ row: { heartbeat } }) =>
            heartbeat?.lastUpdatedTime ? (
                <span>{formatDistance(new Date(heartbeat.lastUpdatedTime), new Date(), { addSuffix: true })}</span>
            ) : null,
    },
];

function DeviceMonitorPage(): JSX.Element {
    const deviceHeartbeatAbortControllerRef = useRef<AbortController | null>(null);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { appCode } = useSelector((store) => store.auth);
    const { locations } = useSelector((state) => state.newLocation);
    const [loading, setLoading] = useState<boolean>(false);
    const [deviceHeartbeatList, setDeviceHeartbeatList] = useState<DeviceHeartbeatWithLocation[]>([]);
    const [filteredDeviceHeartbeatList, setFilteredDeviceHeartbeatList] = useState<DeviceHeartbeatWithLocation[]>([]);
    const [rowsCount, setRowsCount] = useState<number>(0);
    const [connectedCount, setConnectedCount] = useState<number>(0);
    const [disconnectedCount, setDisconnectedCount] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);

    const { name, deviceType, status } = Object.fromEntries(searchParams);

    useEffect(() => {
        getDeviceHeartbeat({
            isMonitored: true,
            pageSize: 10000, // fetch all devices
        });
        return () => {
            deviceHeartbeatAbortControllerRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        setFilteredData(deviceHeartbeatList);
    }, [name, deviceType, status]);

    const setFilteredData = (data: DeviceHeartbeatWithLocation[]) => {
        let filteredData = data;
        if (name?.trim())
            filteredData = filteredData.filter((item) => item.name.toLowerCase().includes(name.trim().toLowerCase()));
        if (
            deviceType?.trim() &&
            Object.values(DeviceType)
                .map((type) => type.toLowerCase())
                .includes(deviceType.trim())
        )
            filteredData = filteredData.filter((item) => item.type.toLowerCase() === deviceType.trim().toLowerCase());
        if (status?.trim() && Object.values(DeviceHeartbeatStatus).includes(status.trim() as DeviceHeartbeatStatus)) {
            const statusFilter = status.trim() as DeviceHeartbeatStatus;
            filteredData =
                statusFilter === DeviceHeartbeatStatus.UNKNOWN
                    ? filteredData.filter(
                          (item) =>
                              !item.heartbeat?.status ||
                              item.heartbeat.status === DeviceHeartbeatStatus.UNKNOWN,
                      )
                    : filteredData.filter((item) => item.heartbeat?.status === statusFilter);
        }
        setFilteredDeviceHeartbeatList(filteredData);
        setRowsCount(filteredData.length);
    };

    const getDeviceHeartbeat = async (queryParams: IGetDeviceHeartbeatQuery) => {
        try {
            setLoading(true);
            deviceHeartbeatAbortControllerRef.current = new AbortController();
            const { signal } = deviceHeartbeatAbortControllerRef.current;
            const response = await executeGetDeviceHeartbeatRequest(queryParams, { signal });
            if (response) {
                const { total, devices } = response;
                const mappedDevices = devices
                    .map((device) => ({
                        ...device,
                        location: locations.find((location) => location.id === device.locationId)?.name || "",
                    }))
                    .sort((a, b) =>
                        (b.heartbeat?.lastUpdatedTime || "").localeCompare(a.heartbeat?.lastUpdatedTime || ""),
                    );
                setDeviceHeartbeatList(mappedDevices);
                setFilteredData(mappedDevices);
                setConnectedCount(
                    mappedDevices.filter((device) => device?.heartbeat?.status === DeviceHeartbeatStatus.CONNECTED)
                        .length,
                );
                setDisconnectedCount(
                    mappedDevices.filter((device) => device?.heartbeat?.status === DeviceHeartbeatStatus.DISCONNECTED)
                        .length,
                );
                setTotalCount(total);
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: Error | any) {
            if (error?.response?.status === StatusCodes.FORBIDDEN) {
                navigate("/forbidden", { replace: true });
                return;
            }
            if (error?.response?.status === StatusCodes.NOT_FOUND) {
                navigate(`/${appCode}/device-monitor/page-not-found`, { replace: true });
                return;
            }
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Grid container>
                <Grid item xs={12} marginBottom={2}>
                    <CustomizedSectionHeading heading="Device Monitor" />
                    <Typography color="text.secondary" fontSize="14px" variant="body">
                        Monitor and manage your devices in real-time.
                    </Typography>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <StatCard title="Total Devices" content={totalCount} isLoading={loading} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <StatCard title="Connected" content={connectedCount} isLoading={loading} contentColor="green" />
                </Grid>
                <Grid item xs={12} md={4}>
                    <StatCard title="Disconnected" content={disconnectedCount} isLoading={loading} contentColor="red" />
                </Grid>
            </Grid>
            <PaperWrapper>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <DeviceMonitorFilterPanel
                            onRefresh={() => {
                                getDeviceHeartbeat({ isMonitored: true, pageSize: 10000 }); // fetch all devices
                            }}
                            isRefreshing={loading}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <IotVisionTable
                            initialState={{
                                sorting: {
                                    sortModel: [{ field: "heartbeat.status", sort: SortOrder.DESC }],
                                },
                            }}
                            loading={loading}
                            sortingMode="client"
                            paginationMode="client"
                            sortingOrder={[SortOrder.ASC, SortOrder.DESC]}
                            columns={tableColumns}
                            rows={filteredDeviceHeartbeatList}
                            pageSize={PAGE_SIZE}
                            rowCount={rowsCount}
                            rowsPerPageOptions={[]}
                            hideFooterPagination={false}
                        />
                    </Grid>
                </Grid>
            </PaperWrapper>
        </>
    );
}

export default DeviceMonitorPage;
